import { Address } from '../address';
import { FantazFailure } from '../failures';
import { UserSettings } from '../profile';

export interface CurrentUser {
  membershipInfo: MembershipInfo;
  user: User;
  status?: string;
}

export interface CustomerRegistration {
  name: string;
  dob: string;
  address: string;
}

export interface MembershipInfo {
  annualSubscription?: boolean;
  lastPaymentDate?: string;
  monthlySubscription?: boolean;
  nextPaymentDate?: string;
  subscriptionFees?: string;
  subscriptionType?: string;
  type?: string;
  zboExpiration?: Date;
  feesPaid?: boolean;
  autoRenew?: boolean;
}

export interface ProfileMatch {
  email: string;
  entityType: string;
}

export interface User {
  businessProfile?: BusinessProfile;
  balance: number;
  loanBalance?: number;
  banner?: string | null;
  bio?: string | null;
  birthday?: string | null;
  cashPlayStatus?: Enum;
  createdAt?: string;
  email: string;
  emailVerified: boolean;
  entityType?: Enum;
  escrowBalance?: number;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string;
  picture?: string | null;
  provider?: string;
  registrationComplete?: boolean;
  sponsorUsername?: string;
  sponsorType?: string;
  taxInfoCollectionStatus?: Enum & { approvalDate?: Date };
  updatedAt?: string;
  username?: string;
  uuid: string;
  verificationStatus?: {
    name: string;
    type: string;
    approvalDate?: Date;
    attempts?: {
      current: {
        DOCUMENT: number;
        CC: number;
        CUSTOMER_IDENTITY: number;
      };
      max: {
        DOCUMENT: number;
        CC: number;
        CUSTOMER_IDENTITY: number;
      };
    };
  };
  version?: number;
  walletFrozen?: boolean;
  walletFrozenReason?: string;
  shippingAddress?: Address;
  legalAddress?: Address;
  userSettings?: UserSettings;
  fantazSettings?: FantazSettings;
  signed_terms_version?: string;
  profileMatch?: ProfileMatch[];
}

export interface BusinessProfile {
  businessName: string;
  classification: {
    name: string;
    type: string;
  };
  stateOfFormation: {
    name: string;
    type: string;
    internalId: number;
  };
}
export interface Enum {
  name: string;
  type: string;
}

export interface UserListResponse {
  list: [
    {
      uuid: string;
      username: string;
    },
  ];
}

export interface UserListRequest {
  usernames: string[];
}

export interface FantazSettings {
  terms: { version: string; acceptedAt: Date }[];
}

export class AccountExistFailure extends FantazFailure {
  constructor(error: Error) {
    super('AccountExistFailure', 'ACCOUNT-1000', error);
  }
}

// *****************************************************************************
// ** Failures for username
// *****************************************************************************
export class UsernameFailure extends FantazFailure {
  constructor(className: string, code: any, error: Error) {
    super(className, code, error);
  }
}

export class InvalidUsernameFailure extends UsernameFailure {
  constructor() {
    super(
      'InvalidUsernameFailure',
      'USERNAME-1001',
      new Error('Invalid username'),
    );
  }
}

export class DuplicateUsernameFailure extends UsernameFailure {
  constructor() {
    super(
      'DuplicateUsernameFailure',
      'USERNAME-1002',
      new Error('Duplicate username'),
    );
  }
}

export class UsernameBlackListFailure extends UsernameFailure {
  constructor() {
    super(
      'UsernameBlackListFailure',
      'USERNAME-1003',
      new Error('Blacklisted username'),
    );
  }
}

// *****************************************************************************
// ** Failures for email
// *****************************************************************************
export class EmailFailure extends FantazFailure {
  constructor(className: string, code: any, error: Error) {
    super(className, code, error);
  }
}

export class InvalidEmailFailure extends EmailFailure {
  constructor() {
    super('InvalidEmailFailure', 'EMAIL-1001', new Error('Invalid username'));
  }
}

export class DuplicateEmailFailure extends EmailFailure {
  constructor() {
    super(
      'DuplicateEmailFailure',
      'EMAIL-1002',
      new Error('Duplicate username'),
    );
  }
}

// *****************************************************************************
// ** Failures for phone number
// *****************************************************************************
export class PhoneNumberFailure extends FantazFailure {
  constructor(className: string, code: any, error: Error) {
    super(className, code, error);
  }
}

export class InvalidPhoneNumberFailure extends PhoneNumberFailure {
  constructor() {
    super(
      'InvalidPhoneNumberFailure',
      'PHONE-1001',
      new Error('Invalid phone number'),
    );
  }
}

export class DuplicatePhoneNumberFailure extends PhoneNumberFailure {
  constructor() {
    super(
      'DuplicatePhoneNumberFailure',
      'PHONE-1002',
      new Error('Duplicate phone number'),
    );
  }
}

// *****************************************************************************
// ** Failures for recaptcha
// *****************************************************************************
export class SignUpValidationFailure extends FantazFailure {
  constructor(error: Error) {
    super('SignUpValidationFailure', 'SIGNUP-1001', error);
  }
}
export class SignUpUserUnconfirmedFailure extends FantazFailure {
  constructor(error: Error) {
    super('SignUpUserUnconfirmedFailure', 'SIGNUP-1001', error);
  }
}

export enum ContactPoint {
  email = 'email',
  phone = 'phone_number',
}

export type AddContactPoint = {
  attr: ContactPoint;
  value: string;
};

export type VerifyContactPoint = AddContactPoint & {
  code: string;
};

export type ResendVerifyContactPoint = VerifyContactPoint;

export type AuthEventsResponse = {
  AuthEvents: AuthUserEvents[];
  NextToken?: string;
};

export type AuthUserEvents = {
  ChallengeResponses: [
    {
      ChallengeName: string;
      ChallengeResponse: string;
    },
  ];
  CreationDate: string;
  EventContextData: {
    City: string;
    Country: string;
    DeviceName: string;
    IpAddress: string;
    Timezone: string;
  };
  EventFeedback: {
    FeedbackDate: number;
    FeedbackValue: string;
    Provider: string;
  };
  EventId: string;
  EventResponse: string;
  EventRisk: {
    CompromisedCredentialsDetected: boolean;
    RiskDecision: string;
    RiskLevel: string;
  };
  EventType: string;
  LastAuthenticatedInterval: string;
};
